import { useState } from "react";
import { ChevronDown, ChevronRight, Mail, Cable, Inbox, Webhook } from "lucide-react";

function statusBadgeClass(s) {
    if (!s) return "";
    if (s === "dry_run" || s === "dry_run_email" || s === "no_match") return "warn";
    if (s === "sent") return "ok";
    if (s === "error" || s === "failed" || s === "invalid_payload") return "danger";
    if (/^2\d\d$/.test(String(s))) return "ok";
    if (/^[45]\d\d$/.test(String(s))) return "danger";
    return "";
}

function IngressPill({ type }) {
    const isEmail = type === "email";
    return (
        <span
            className="badge"
            style={{
                color: isEmail ? "var(--info)" : "var(--accent)",
                borderColor: isEmail
                    ? "rgba(125,211,252,0.35)"
                    : "rgba(124,255,203,0.35)",
                background: isEmail
                    ? "rgba(125,211,252,0.05)"
                    : "rgba(124,255,203,0.05)",
            }}
        >
            {isEmail ? <Inbox size={10} /> : <Webhook size={10} />}
            in:{type || "?"}
        </span>
    );
}

function EgressPill({ type, status }) {
    if (!type)
        return (
            <span className="badge warn" title="no rule matched">
                out:none
            </span>
        );
    const isEmail = type === "email";
    return (
        <span
            className="badge"
            style={{
                color: isEmail ? "var(--info)" : "var(--accent)",
                borderColor: isEmail
                    ? "rgba(125,211,252,0.35)"
                    : "rgba(124,255,203,0.35)",
                background: isEmail
                    ? "rgba(125,211,252,0.05)"
                    : "rgba(124,255,203,0.05)",
            }}
        >
            {isEmail ? <Mail size={10} /> : <Cable size={10} />}
            out:{isEmail ? (status === "dry_run_email" ? "email·dry" : "email") : "glip"}
        </span>
    );
}

function formatTs(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toISOString().replace("T", " ").slice(0, 19) + " UTC";
    } catch {
        return iso;
    }
}

function JsonBlock({ data, testid }) {
    return (
        <pre
            className="mono text-[11px] leading-relaxed p-4 rounded-md overflow-x-auto"
            style={{
                background: "#05080c",
                border: "1px solid var(--border)",
                color: "var(--text)",
                maxHeight: "420px",
            }}
            data-testid={testid}
        >
            {JSON.stringify(data, null, 2)}
        </pre>
    );
}

function PlainBlock({ text, testid, maxHeight = "420px" }) {
    return (
        <pre
            className="mono text-[11px] leading-relaxed p-4 rounded-md overflow-auto whitespace-pre-wrap"
            style={{
                background: "#05080c",
                border: "1px solid var(--border)",
                color: "var(--text)",
                maxHeight,
            }}
            data-testid={testid}
        >
            {text}
        </pre>
    );
}

function EmailOutboundView({ delivery }) {
    const [tab, setTab] = useState("plain");
    const [copied, setCopied] = useState(false);
    const html = delivery.transformed_body?.html || "";
    const body = delivery.transformed_body?.body || "";
    const subject = delivery.transformed_body?.subject || "—";
    const toMasked = delivery.outbound_url_redacted || "—";
    const shortUrl = delivery.short_details_url || "";

    const copyShort = async () => {
        if (!shortUrl) return;
        try {
            await navigator.clipboard.writeText(shortUrl);
            setCopied(true);
            setTimeout(() => setCopied(false), 1400);
        } catch {
            /* ignore */
        }
    };

    return (
        <div className="flex flex-col gap-2">
            <div
                className="mono text-[11px]"
                style={{ color: "var(--text-dim)" }}
            >
                Subject:{" "}
                <span style={{ color: "var(--text)" }}>{subject}</span>
                <br />
                To:{" "}
                <span style={{ color: "var(--text)" }}>{toMasked}</span>
                {shortUrl ? (
                    <>
                        <br />
                        Short link:{" "}
                        <a
                            href={shortUrl}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: "var(--accent)" }}
                            data-testid={`short-link-${delivery.id}`}
                        >
                            {shortUrl}
                        </a>{" "}
                        <button
                            type="button"
                            onClick={copyShort}
                            className="ghost-btn"
                            style={{ padding: "2px 8px", fontSize: 10 }}
                            data-testid={`copy-short-url-${delivery.id}`}
                        >
                            {copied ? "copied" : "copy short URL"}
                        </button>
                    </>
                ) : null}
            </div>
            <div
                className="flex items-center gap-1"
                role="tablist"
                data-testid={`outbound-email-tabs-${delivery.id}`}
            >
                <button
                    role="tab"
                    aria-selected={tab === "plain"}
                    onClick={() => setTab("plain")}
                    className="mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-md"
                    style={{
                        color:
                            tab === "plain"
                                ? "var(--accent)"
                                : "var(--text-dim)",
                        background:
                            tab === "plain"
                                ? "rgba(124,255,203,0.06)"
                                : "transparent",
                        border:
                            tab === "plain"
                                ? "1px solid rgba(124,255,203,0.35)"
                                : "1px solid var(--border-strong)",
                    }}
                    data-testid={`outbound-email-tab-plain-${delivery.id}`}
                >
                    Plain
                </button>
                <button
                    role="tab"
                    aria-selected={tab === "html"}
                    onClick={() => setTab("html")}
                    disabled={!html}
                    className="mono text-[10px] uppercase tracking-[0.18em] px-3 py-1.5 rounded-md disabled:opacity-50"
                    style={{
                        color:
                            tab === "html"
                                ? "var(--info)"
                                : "var(--text-dim)",
                        background:
                            tab === "html"
                                ? "rgba(125,211,252,0.06)"
                                : "transparent",
                        border:
                            tab === "html"
                                ? "1px solid rgba(125,211,252,0.35)"
                                : "1px solid var(--border-strong)",
                    }}
                    data-testid={`outbound-email-tab-html-${delivery.id}`}
                    title={html ? "" : "No HTML part stored"}
                >
                    HTML
                </button>
                {html && (
                    <span
                        className="mono text-[10px] ml-2"
                        style={{ color: "var(--text-faint)" }}
                    >
                        {Math.round(html.length / 1024)} KB
                    </span>
                )}
            </div>
            {tab === "plain" ? (
                <PlainBlock
                    text={body}
                    testid={`outbound-email-${delivery.id}`}
                />
            ) : (
                <iframe
                    title={`email-html-${delivery.id}`}
                    srcDoc={html}
                    sandbox=""
                    data-testid={`outbound-email-html-${delivery.id}`}
                    style={{
                        width: "100%",
                        height: "460px",
                        border: "1px solid var(--border)",
                        borderRadius: "6px",
                        background: "#ffffff",
                    }}
                />
            )}
        </div>
    );
}

function AlertSummary({ alert }) {
    if (!alert) return null;
    const sev = alert.severity || "INFO";
    const sevColor =
        sev === "CRITICAL"
            ? "var(--danger)"
            : sev === "HIGH"
              ? "var(--warn)"
              : "var(--accent)";
    return (
        <div className="grid grid-cols-2 gap-2 mono text-[11px]">
            <div>
                <span style={{ color: "var(--text-faint)" }}>alert_type</span>{" "}
                <span style={{ color: "var(--text)" }}>
                    {alert.alert_type || "—"}
                </span>
            </div>
            <div>
                <span style={{ color: "var(--text-faint)" }}>severity</span>{" "}
                <span style={{ color: sevColor }}>{sev}</span>
            </div>
            <div>
                <span style={{ color: "var(--text-faint)" }}>driver</span>{" "}
                <span style={{ color: "var(--text)" }}>
                    {alert.driver_name || "—"}
                </span>
                {alert.driver_phone ? (
                    <span style={{ color: "var(--text-faint)" }}>
                        {" "}
                        ({alert.driver_phone})
                    </span>
                ) : null}
            </div>
            <div>
                <span style={{ color: "var(--text-faint)" }}>vehicle</span>{" "}
                <span style={{ color: "var(--text)" }}>
                    {alert.vehicle_name || "—"}
                </span>
            </div>
            <div className="col-span-2">
                <span style={{ color: "var(--text-faint)" }}>location</span>{" "}
                <span style={{ color: "var(--text)" }}>
                    {alert.location || "—"}
                </span>
            </div>
            <div className="col-span-2">
                <span style={{ color: "var(--text-faint)" }}>triggered</span>{" "}
                <span style={{ color: "var(--text)" }}>
                    {alert.triggered_at || "—"}
                </span>
            </div>
            {alert.see_details_url && (
                <div className="col-span-2 break-all">
                    <span style={{ color: "var(--text-faint)" }}>see_details</span>{" "}
                    <a
                        href={alert.see_details_url}
                        target="_blank"
                        rel="noreferrer"
                        style={{ color: "var(--accent)" }}
                    >
                        {alert.see_details_url}
                    </a>
                </div>
            )}
        </div>
    );
}

export default function DeliveryRow({ delivery, expanded, onToggle }) {
    const ingress = delivery.ingress_type || "webhook";
    const egress = delivery.egress_type;
    const tone = statusBadgeClass(delivery.outbound_status);
    const isEmailIngress = ingress === "email";
    const isEmailEgress = egress === "email";

    return (
        <div
            className="panel-inset overflow-hidden"
            data-testid={`delivery-row-${delivery.id}`}
        >
            <button
                onClick={onToggle}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-white/[0.02] transition-colors"
                data-testid={`delivery-toggle-${delivery.id}`}
            >
                <span style={{ color: "var(--text-faint)", width: 14 }}>
                    {expanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                </span>
                <span
                    className="mono text-[11px]"
                    style={{ color: "var(--text-faint)", minWidth: "168px" }}
                >
                    {formatTs(delivery.created_at)}
                </span>
                <IngressPill type={ingress} />
                <EgressPill type={egress} status={delivery.outbound_status} />
                <span
                    className="mono text-xs flex-1 truncate"
                    style={{ color: "var(--text)" }}
                >
                    {isEmailIngress
                        ? delivery.alert?.alert_type || delivery.event_type || "—"
                        : delivery.event_type || "—"}
                </span>
                <span
                    className="mono text-[11px] hidden md:inline truncate"
                    style={{
                        color: delivery.matched_rule
                            ? "var(--accent)"
                            : "var(--text-faint)",
                        width: 140,
                    }}
                    data-testid={`delivery-rule-${delivery.id}`}
                >
                    {delivery.matched_rule || "—"}
                </span>
                <span
                    className="mono text-[11px] hidden md:inline truncate"
                    style={{ color: "var(--text-dim)", width: 96 }}
                >
                    {delivery.channel_name || "—"}
                </span>
                <span
                    className={`badge ${tone}`}
                    data-testid={`delivery-status-${delivery.id}`}
                >
                    <span className="dot" />
                    {delivery.outbound_status}
                </span>
            </button>

            {expanded && (
                <div className="px-4 pb-4 pt-1 fade-in">
                    {/* Email ingress: alert summary block */}
                    {isEmailIngress && delivery.alert && (
                        <div className="mb-3 panel-inset p-3">
                            <div
                                className="mono text-[10px] uppercase tracking-[0.2em] mb-2"
                                style={{ color: "var(--text-faint)" }}
                            >
                                parsed alert · email ingress
                            </div>
                            <AlertSummary alert={delivery.alert} />
                        </div>
                    )}

                    <div className="grid lg:grid-cols-2 gap-4">
                        {/* Inbound side */}
                        <div>
                            <div
                                className="mono text-[10px] uppercase tracking-[0.2em] mb-2"
                                style={{ color: "var(--text-faint)" }}
                            >
                                inbound · {ingress === "email" ? "samsara email" : "samsara webhook"}
                            </div>
                            {isEmailIngress ? (
                                <div className="flex flex-col gap-2">
                                    <div
                                        className="mono text-[11px]"
                                        style={{ color: "var(--text-dim)" }}
                                    >
                                        From: {delivery.email_meta?.from || "—"}
                                        <br />
                                        Subject: {delivery.email_meta?.subject || "—"}
                                        <br />
                                        Message-ID: {delivery.email_meta?.message_id || "—"}
                                    </div>
                                    <PlainBlock
                                        text={
                                            delivery.email_meta?.html_truncated ||
                                            "(no html body captured)"
                                        }
                                        testid={`inbound-html-${delivery.id}`}
                                        maxHeight="320px"
                                    />
                                </div>
                            ) : (
                                <JsonBlock
                                    data={delivery.inbound_body}
                                    testid={`inbound-body-${delivery.id}`}
                                />
                            )}
                        </div>

                        {/* Outbound side */}
                        <div>
                            <div
                                className="mono text-[10px] uppercase tracking-[0.2em] mb-2"
                                style={{ color: "var(--text-faint)" }}
                            >
                                outbound ·{" "}
                                {isEmailEgress
                                    ? "plaintext email"
                                    : egress === "glip_webhook"
                                      ? "glip card"
                                      : "(no egress)"}
                            </div>
                            {isEmailEgress ? (
                                <EmailOutboundView delivery={delivery} />
                            ) : (
                                <JsonBlock
                                    data={delivery.transformed_body}
                                    testid={`transformed-body-${delivery.id}`}
                                />
                            )}
                        </div>
                    </div>

                    <div className="mt-3 panel-inset p-3">
                        <div
                            className="mono text-[10px] uppercase tracking-[0.2em] mb-1"
                            style={{ color: "var(--text-faint)" }}
                        >
                            outbound result
                        </div>
                        <div
                            className="mono text-[11px] break-all"
                            style={{ color: "var(--text-dim)" }}
                        >
                            rule: {delivery.matched_rule || "—"} · channel:{" "}
                            {delivery.channel_name || "—"} · type:{" "}
                            {delivery.channel_type || "—"} · mode:{" "}
                            {delivery.outbound_mode} · status:{" "}
                            {delivery.outbound_status} · target:{" "}
                            {delivery.outbound_url_redacted || "(none)"}
                        </div>
                        {delivery.outbound_response_text && (
                            <pre
                                className="mono text-[11px] mt-2 p-3 rounded-md overflow-x-auto"
                                style={{
                                    background: "#05080c",
                                    border: "1px solid var(--border)",
                                    color: "var(--text-dim)",
                                    maxHeight: "160px",
                                }}
                            >
                                {delivery.outbound_response_text}
                            </pre>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}
